/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber, defineMessage, useIntl} from 'react-intl'
import {
    Box,
    Flex,
    Button,
    Stack,
    Text,
    Divider,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    useBreakpointValue,
    ListItem
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {HStack, Portal, UnorderedList} from '@chakra-ui/react'
import Link from '@salesforce/retail-react-app/app/components/link'
import {PromoCode, usePromoCode} from '../promo-code'
import ItemVariantProvider from '../item-variant'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import CartItemVariantPrice from '../item-variant/item-price'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {HideOnDesktop} from '@salesforce/retail-react-app/app/components/responsive'
import {PlusIcon, MinusIcon} from '../custom-icons'
import FocusLock from 'react-focus-lock'
import {getEstimationDate} from '../../utils/basket-utils'
import useMultiSite from '../../hooks/use-multi-site'
import {GC_SHIPMENT_ID} from '../../constants'
import GiftCertificateItem from '../gift-certificate-item'

// custom hooks
import {useConversionRate} from '../../custom-contexts'
import useSiteType from '../../hooks/use-site-type'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import {useGiftCardLogic} from '../../hooks/use-gift-card-logic'
import useShipToCountry from '../../hooks/use-ship-to-country'

// utils
import {
    getConvertedCurrencyCode,
    getConvertedPrice,
    getOrderConversionRate,
    formatPrice,
    isIncludedTax,
    showOtherTaxAndDuties,
    isZonosTaxesUsed
} from '../../utils/price-utils'

const ORDER_SUMMARY_TITLE = defineMessage({
    id: 'order_summary.heading.order_summary',
    defaultMessage: 'Order Summary'
})

/**
 * SummaryAccordion is a React functional component that renders an accordion interface
 * for displaying a summary of items in a shopping basket. It provides a toggleable view
 * for users to expand and collapse the list of items, along with additional details such
 * as promotional code forms and delivery lines.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.basket - The basket object containing items to be displayed in the summary.
 * @param {boolean} [props.showPromoCodeForm=true] - A flag to determine whether the promo code form should be displayed.
 * @param {boolean} [props.showDeliveryLine=true] - A flag to determine whether the delivery line should be displayed.
 *
 * @returns {JSX.Element} The rendered accordion component displaying the order summary.
 *
 * The component uses responsive design principles to adjust its layout based on the screen size.
 * It utilizes the `useBreakpointValue` hook to determine if the device is mobile and adjusts
 * the display of the accordion accordingly. The accordion's expanded state is managed using
 * the `useState` hook, and the `FocusLock` component is used to manage focus when the accordion
 * is expanded on mobile devices.
 *
 * The accordion includes an `AccordionButton` that toggles the visibility of the `AccordionPanel`,
 * which contains the `CartItems` component to display the items in the basket. On mobile devices,
 * a semi-transparent overlay is displayed when the accordion is expanded, allowing users to click
 * outside the panel to collapse it. The `TotalsSummary` component is conditionally rendered based
 * on the `showPromoCodeForm` and `showDeliveryLine` flags.
 */
const SummaryAccordion = ({
    basket,
    showPromoCodeForm = true,
    showDeliveryLine = true,
    isBuyingOnlyGiftCards,
    giftCertificateLineItemCost,
    currentBasket,
    totalItems
}) => {
    const [index, setIndex] = useState(-1)
    const isMobile = useBreakpointValue({base: true, lg: false})
    const conversionsRate = useConversionRate()
    const {shipToCountry} = useShipToCountry()
    const intl = useIntl()
    const isInternationalShipping = currentBasket?.c_internationalShipping || false

    return (
        <>
            <Accordion
                allowToggle={true}
                width="100%"
                display={{base: 'block', lg: 'block'}}
                index={index}
                onChange={setIndex}
            >
                <AccordionItem style={{border: 0}}>
                    {({isExpanded}) => (
                        <>
                            <FocusLock disabled={!isMobile || !isExpanded}>
                                <AccordionButton
                                    justifyContent={'space-between'}
                                    alignItems={'flex-start'}
                                    p={['12px 16px', null, null, 0]}
                                    height={[55, null, null, 31]}
                                    zIndex={1}
                                    position={'relative'}
                                >
                                    <Box textAlign={'left'}>
                                        <Text variant="descriptor" textTransform="uppercase">
                                            <FormattedMessage
                                                id="order_summary.heading.order_summary.items"
                                                description="clicking it would expand/show the items in cart"
                                                defaultMessage="Order Summary ({itemCount})"
                                                values={{itemCount: totalItems}}
                                            />
                                        </Text>
                                        <Text variant="bodyBase1" color="darkGray" mt={2}>
                                            {formatPrice(
                                                isInternationalShipping &&
                                                    currentBasket?.c_internationalProductsSubTotal
                                                    ? currentBasket?.c_internationalProductsSubTotal
                                                    : getConvertedPrice(
                                                          currentBasket?.productSubTotal +
                                                              giftCertificateLineItemCost,
                                                          conversionsRate,
                                                          false
                                                      ),
                                                getConvertedCurrencyCode(
                                                    conversionsRate,
                                                    currentBasket?.currency
                                                ),
                                                conversionsRate,
                                                shipToCountry?.id,
                                                intl
                                            )}
                                        </Text>
                                    </Box>
                                    <HStack>
                                        <Text
                                            variant={'eyeBrowSmall'}
                                            textTransform={'uppercase'}
                                            whiteSpace={'nowrap'}
                                        >
                                            <FormattedMessage
                                                id="order_summary.cart_items.action.view_items"
                                                description="clicking it would expand/show the items in cart"
                                                defaultMessage="View items"
                                            />
                                        </Text>
                                        {isExpanded ? (
                                            <MinusIcon boxSize="12px" ml={4} mb={2} />
                                        ) : (
                                            <PlusIcon boxSize="12px" ml={4} mb={2} />
                                        )}
                                    </HStack>
                                </AccordionButton>

                                {isExpanded && (
                                    <AccordionPanel
                                        px={[16, null, null, 0]}
                                        pb={[16, null, null, 8]}
                                        position={['absolute', null, null, 'initial']}
                                        bg="white"
                                        borderColor="borderGray"
                                        borderBottomWidth={[1, null, null, 0]}
                                        w="full"
                                    >
                                        <Portal>
                                            <Box
                                                w="100vw"
                                                position={'fixed'}
                                                height={'100vh'}
                                                top={0}
                                                left={0}
                                                backgroundColor={'black'}
                                                zIndex={1}
                                                opacity={0.5}
                                                transition={'opacity 0.2s'}
                                                display={isMobile && isExpanded ? 'block' : 'none'}
                                                onClick={() => setIndex(-1)}
                                            />
                                        </Portal>
                                        <Divider
                                            color="borderGray"
                                            display={['none', null, null, 'block']}
                                            my={16}
                                        />
                                        <CartItems
                                            basket={basket}
                                            isBuyingOnlyGiftCards={isBuyingOnlyGiftCards}
                                        />
                                        <HideOnDesktop>
                                            <Divider color="borderGray" my={16} />
                                            <TotalsSummary
                                                basket={basket}
                                                showPromoCodeForm={showPromoCodeForm}
                                                showDeliveryLine={showDeliveryLine}
                                                giftCertificateLineItemCost={
                                                    giftCertificateLineItemCost
                                                }
                                            />
                                        </HideOnDesktop>
                                    </AccordionPanel>
                                )}
                            </FocusLock>
                        </>
                    )}
                </AccordionItem>
            </Accordion>
        </>
    )
}

SummaryAccordion.propTypes = {
    basket: PropTypes.object,
    showPromoCodeForm: PropTypes.bool,
    showDeliveryLine: PropTypes.bool,
    isBuyingOnlyGiftCards: PropTypes.bool,
    giftCertificateLineItemCost: PropTypes.number,
    currentBasket: PropTypes.object,
    totalItems: PropTypes.number
}

/**
 * CartItems is a React functional component that renders a summary of items in a shopping cart.
 * It displays the products grouped by their shipment details and provides an option to edit the cart.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.basket - The basket object containing details of the cart items and shipments.
 * @param {Array} props.basket.productItems - An array of product items in the basket.
 * @param {Array} props.basket.shipments - An array of shipment details for the products in the basket.
 * @param {string} props.basket.currency - The currency used for the product prices in the basket.
 *
 * @returns {JSX.Element} A React component that displays the cart items and their shipment information.
 *
 * The component uses the `useProducts` hook to fetch product details based on the product IDs present in the basket.
 * It maps over the shipments and product items to render each product's details, including its image, name, price, and attributes.
 * The component also includes a button that links to the cart page for editing the cart.
 *
 * Note: The component assumes that the `FormattedMessage` component is used for internationalization of text.
 * The `ItemVariantProvider` is used to provide context for each product variant displayed.
 *
 */
const CartItems = ({basket, isBuyingOnlyGiftCards}) => {
    const {locale} = useMultiSite()
    const conversionsRate = useConversionRate()
    const intl = useIntl()
    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: products} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true
            }
        },
        {
            enabled: Boolean(productIds),
            select: (result) => {
                // Convert array into key/value object with key is the product id
                return result?.data?.reduce((result, item) => {
                    const key = item?.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    // Trier les shipments pour que celui avec GC_SHIPMENT_ID soit en dernier
    const sortedShipments = basket?.shipments?.slice().sort((a, b) => {
        if (a.shipmentId === GC_SHIPMENT_ID) return 1
        if (b.shipmentId === GC_SHIPMENT_ID) return -1
        return 0
    })

    return (
        <>
            <UnorderedList
                listStyleType="none"
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
            >
                {sortedShipments?.map((shipment, index) => {
                    /**
                     * GCs will have the shipmentId as follows:
                     * If buying only GCs -> shipmentId === DEFAULT_SHIPMENT_ID
                     * If mixed cart      -> shipmentId === GC_SHIPMENT_ID
                     */
                    const isVirtualItem =
                        isBuyingOnlyGiftCards || shipment.shipmentId === GC_SHIPMENT_ID
                    return (
                        <ListItem key={shipment.shipmentId + index} w="full">
                            <Text variant={'eyeBrowSmall'} textTransform={'uppercase'}>
                                {isVirtualItem ? (
                                    <FormattedMessage
                                        defaultMessage="Virtual {itemCount, plural, =0 {items} one {item} other {items}}"
                                        id="order_summary.cart_items.virtual_shipment.title"
                                        values={{
                                            itemCount: basket.giftCertificateItems?.filter(
                                                (item) => item.shipmentId === shipment.shipmentId
                                            )?.length
                                        }}
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Shipment {index}"
                                        id="order_summary.cart_items.physical_shipment.title"
                                        values={{index: index + 1}}
                                    />
                                )}
                            </Text>
                            {!isVirtualItem && (
                                <>
                                    <Flex mt={8}>
                                        {
                                            <>
                                                <Text
                                                    as="span"
                                                    color="darkGray"
                                                    variant="bodyBase2"
                                                >
                                                    {shipment?.c_shipFromStore ||
                                                    shipment?.c_clickAndCollectToday ? (
                                                        <FormattedMessage
                                                            defaultMessage="Shipped from"
                                                            id="order_summary.cart_items.shipment.store_inventory.from"
                                                        />
                                                    ) : (
                                                        <FormattedMessage
                                                            defaultMessage="Shipped from"
                                                            id="order_summary.cart_items.shipment.warehouse_inventory.from"
                                                        />
                                                    )}
                                                    :&nbsp;
                                                </Text>
                                                <Text as="span" variant="bodyBase2">
                                                    {shipment?.c_shipFromStore ||
                                                    shipment?.c_clickAndCollectToday ? (
                                                        <FormattedMessage
                                                            defaultMessage="Vilebrequin Store"
                                                            id="order_summary.cart_items.shipment.store_inventory.store_name"
                                                        />
                                                    ) : (
                                                        <FormattedMessage
                                                            defaultMessage="Vilebrequin Warehouse"
                                                            id="order_summary.cart_items.shipment.warehouse_inventory.store_name"
                                                        />
                                                    )}
                                                </Text>
                                            </>
                                        }
                                    </Flex>
                                    {!shipment?.c_clickAndCollectToday && (
                                        <Text variant="bodyBase2" color="darkGray" mt={4}>
                                            {getEstimationDate(
                                                shipment?.shippingMethod,
                                                locale?.id,
                                                intl
                                            )}
                                        </Text>
                                    )}
                                </>
                            )}

                            <Stack
                                as="ul"
                                spacing={24}
                                align="flex-start"
                                w="full"
                                mx="auto"
                                listStyleType={'none'}
                                mt={16}
                            >
                                {/* Product items */}
                                {basket.productItems?.map((product, idx) => {
                                    const variant = {
                                        ...product,
                                        ...products?.[product.productId],
                                        price: product.price
                                    }
                                    // Exclude unselected bonus gift products
                                    if (
                                        product?.shipmentId === shipment.shipmentId &&
                                        (!product?.bonusProductLineItem || product?.c_sendThisGift)
                                    ) {
                                        const currentAriaLabel = product.bonusProductLineItem
                                            ? intl.formatMessage({
                                                  defaultMessage: 'Bonus product',
                                                  id: 'gift_with_purchase.label.bonus'
                                              })
                                            : intl.formatMessage({
                                                  defaultMessage: 'Product',
                                                  id: 'add_to_cart_modal.aria_label.product'
                                              })
                                        return (
                                            <ItemVariantProvider
                                                key={product.productId}
                                                index={idx}
                                                variant={variant}
                                            >
                                                <Flex as="li" width="full" alignItems="flex-start">
                                                    <Flex
                                                        width="full"
                                                        alignItems="flex-start"
                                                        role="group"
                                                        aria-label={currentAriaLabel}
                                                    >
                                                        <CartItemVariantImage
                                                            width={'85px'}
                                                            mr={16}
                                                        />
                                                        <Stack spacing={8} flex={1}>
                                                            {product.bonusProductLineItem && (
                                                                <Text
                                                                    variant="eyeBrowSmall"
                                                                    textTransform={'uppercase'}
                                                                >
                                                                    <FormattedMessage
                                                                        defaultMessage="Bonus product"
                                                                        id="gift_with_purchase.label.bonus"
                                                                    />
                                                                </Text>
                                                            )}
                                                            <CartItemVariantName isSmall />
                                                            <CartItemVariantPrice
                                                                currency={getConvertedCurrencyCode(
                                                                    conversionsRate,
                                                                    basket?.currency
                                                                )}
                                                            />
                                                            <CartItemVariantAttributes
                                                                includeQuantity
                                                                mt={8}
                                                            />
                                                        </Stack>
                                                    </Flex>
                                                </Flex>
                                            </ItemVariantProvider>
                                        )
                                    }
                                })}

                                {/* Gift Cards */}
                                {basket.giftCertificateItems
                                    ?.filter((gc) => gc.shipmentId === shipment.shipmentId)
                                    .map((gc) => {
                                        return (
                                            <ListItem key={gc.giftCertificateItemId}>
                                                <GiftCertificateItem
                                                    giftCertificateItem={gc}
                                                    imageStyles={{width: '85px'}}
                                                    headingStyles={{fontSize: '2xl'}}
                                                />
                                            </ListItem>
                                        )
                                    })}
                            </Stack>
                            <Divider color="borderGray" my={16} />
                        </ListItem>
                    )
                })}
            </UnorderedList>
            <Box textAlign={'center'} pb={[12, null, null, 8]}>
                <Button as={Link} to={'/cart'} variant="link" width="full">
                    <FormattedMessage
                        defaultMessage="Edit cart"
                        id="order_summary.cart_items.link.edit_cart"
                    />
                </Button>
            </Box>
        </>
    )
}

CartItems.propTypes = {
    basket: PropTypes.object,
    isBuyingOnlyGiftCards: PropTypes.bool
}

/**
 * TotalsSummary is a React functional component that displays a summary of the order totals,
 * including subtotals, taxes, duties, delivery charges, and any applicable promotions.
 * It provides a detailed breakdown of the costs associated with a shopping basket.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.basket - The basket object containing details of the shopping cart.
 * @param {boolean} [props.showPromoCodeForm=true] - Determines whether to display the promo code form.
 * @param {boolean} [props.showDeliveryLine=true] - Determines whether to display the delivery line.
 *
 * @returns {JSX.Element} A JSX element representing the order summary.
 *
 * @example
 * <TotalsSummary basket={basketData} showPromoCodeForm={false} showDeliveryLine={true} />
 *
 * @component
 * @name TotalsSummary
 *
 * @description
 * The component uses the `usePromoCode` hook to manage promo code functionalities,
 * and it conditionally renders various sections such as subtotal, tax, duties, delivery,
 * and promotions based on the provided basket data. It also handles the display of
 * promotional discounts and calculates the total order amount.
 */
const TotalsSummary = ({basket, showPromoCodeForm = true, giftCertificateLineItemCost}) => {
    // Using destructuring to remove properties from the object...
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {removePromoCode, ...promoCodeProps} = usePromoCode()
    const intl = useIntl()
    const {getGiftcardPaymentInstruments} = useGiftCardLogic()
    const {isUS} = useSiteType()

    const shippingItem = basket.shippingItems?.[0]
    const hasShippingPromos = shippingItem?.priceAdjustments?.length > 0
    const isInternationalShipping = basket?.c_internationalShipping || false

    const conversionsRate = getOrderConversionRate(basket)
    const shipToCountryCode = basket?.c_internationalShippingCountryCode

    return (
        <>
            <Stack w="full" spacing={16} mt="10px">
                {/** Subtotal */}
                <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                    <Text variant="bodyLarge2" color="darkGray">
                        <FormattedMessage
                            defaultMessage="Subtotal"
                            id="order_summary.label.subtotal"
                        />
                        :
                    </Text>
                    <Text variant="bodyLarge1">
                        {formatPrice(
                            isInternationalShipping && basket?.c_internationalProductsSubTotal
                                ? basket?.c_internationalProductsSubTotal
                                : getConvertedPrice(
                                      basket?.productSubTotal + giftCertificateLineItemCost,
                                      conversionsRate,
                                      false
                                  ),
                            getConvertedCurrencyCode(conversionsRate, basket?.currency),
                            conversionsRate,
                            shipToCountryCode,
                            intl
                        )}
                    </Text>
                </Flex>

                {/** Promotions */}
                {basket.orderPriceAdjustments?.map((adjustment) => (
                    <Flex
                        justify="space-between"
                        key={adjustment.priceAdjustmentId}
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        <Text variant="bodyLarge2" color="darkGray">
                            {adjustment.itemText}
                        </Text>
                        <Text variant="bodyLarge1">
                            {formatPrice(
                                getConvertedPrice(adjustment?.price, conversionsRate),
                                getConvertedCurrencyCode(conversionsRate, basket?.currency),
                                conversionsRate,
                                shipToCountryCode,
                                intl
                            )}
                        </Text>
                    </Flex>
                ))}

                {/** Delivery */}
                {
                    <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                        <Flex alignItems="center">
                            <Text lineHeight={1} variant="bodyLarge2" color="darkGray">
                                <FormattedMessage
                                    defaultMessage="Delivery"
                                    id="order_summary.label.shipping"
                                />
                                :
                                {hasShippingPromos && (
                                    <Text as="span" ml={1}>
                                        (
                                        <FormattedMessage
                                            defaultMessage="Promotion applied"
                                            id="order_summary.label.promo_applied"
                                        />
                                        )
                                    </Text>
                                )}
                            </Text>
                        </Flex>

                        {shippingItem?.priceAdjustments?.some(
                            ({appliedDiscount}) => appliedDiscount?.type === 'free'
                        ) || basket?.shippingTotal === 0 ? (
                            <Text variant="bodyLarge1" textTransform={'uppercase'}>
                                <FormattedMessage
                                    defaultMessage="Free"
                                    id="order_summary.label.free"
                                />
                            </Text>
                        ) : (
                            <Text variant="bodyLarge1">
                                {formatPrice(
                                    getConvertedPrice(
                                        basket?.shippingTotal === null ? 0 : basket?.shippingTotal,
                                        conversionsRate,
                                        false,
                                        true
                                    ),
                                    getConvertedCurrencyCode(conversionsRate, basket?.currency),
                                    conversionsRate,
                                    shipToCountryCode,
                                    intl
                                )}
                            </Text>
                        )}
                    </Flex>
                }

                {/** Tax Comming From Zonos */}
                {!isUS &&
                    isInternationalShipping &&
                    !isIncludedTax(shipToCountryCode) &&
                    isZonosTaxesUsed(shipToCountryCode) && (
                        <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                            <Text variant="bodyLarge2" color="darkGray">
                                <FormattedMessage
                                    defaultMessage="Tax"
                                    id="order_summary.label.tax"
                                />
                                :
                            </Text>
                            {basket?.c_internationalShippingZonosTax != null ? (
                                <Text variant="bodyLarge1">
                                    {formatPrice(
                                        basket?.c_internationalShippingZonosTax,
                                        getConvertedCurrencyCode(conversionsRate, basket?.currency),
                                        conversionsRate,
                                        shipToCountryCode,
                                        intl
                                    )}
                                </Text>
                            ) : (
                                <Text variant="bodyLarge1">
                                    {formatPrice(
                                        getConvertedPrice(0, conversionsRate, false),
                                        getConvertedCurrencyCode(conversionsRate, basket?.currency),
                                        conversionsRate,
                                        shipToCountryCode,
                                        intl
                                    )}
                                </Text>
                            )}
                        </Flex>
                    )}

                {/** Sales Tax (US): */}
                {isUS && (
                    <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                        <Text variant="bodyLarge2" color="darkGray">
                            <FormattedMessage
                                defaultMessage="Sales Tax"
                                id="order_summary.label.sales.tax"
                            />
                            :
                        </Text>
                        <Text variant="bodyLarge1">
                            {formatPrice(
                                getConvertedPrice(basket?.taxTotal || 0, conversionsRate, false),
                                getConvertedCurrencyCode(conversionsRate, basket?.currency),
                                conversionsRate,
                                shipToCountryCode,
                                intl
                            )}
                        </Text>
                    </Flex>
                )}

                {/** Other tax and duties: */}
                {showOtherTaxAndDuties(shipToCountryCode) && (
                    <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                        <Text variant="bodyLarge2" color="darkGray">
                            <FormattedMessage
                                defaultMessage="Other tax and duties"
                                id="order_summary.label.tax.and.duties"
                            />
                            :
                        </Text>
                        {basket?.c_internationalShippingZonosDuties != null ? (
                            <Text variant="bodyLarge1">
                                {formatPrice(
                                    basket?.c_internationalShippingZonosDuties,
                                    getConvertedCurrencyCode(conversionsRate, basket?.currency),
                                    conversionsRate,
                                    shipToCountryCode,
                                    intl
                                )}
                            </Text>
                        ) : (
                            <Text variant="bodyLarge1">
                                {formatPrice(
                                    getConvertedPrice(0, conversionsRate, false),
                                    getConvertedCurrencyCode(conversionsRate, basket?.currency),
                                    conversionsRate,
                                    shipToCountryCode,
                                    intl
                                )}
                            </Text>
                        )}
                    </Flex>
                )}

                {/* Gift Cards */}
                {getGiftcardPaymentInstruments(basket).map((gc) => (
                    <Flex
                        justify="space-between"
                        key={gc.paymentInstrumentId}
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        <Text variant="bodyLarge2" color="darkGray">
                            <FormattedMessage
                                defaultMessage="Online Gift Card"
                                id="giftcard.name"
                            />{' '}
                            <Text as="span" sx={{fontFamily: 'monospace'}}>
                                {gc.maskedGiftCertificateCode &&
                                    gc.maskedGiftCertificateCode.slice(-6)}
                            </Text>
                            :
                        </Text>
                        <Text variant="bodyLarge1">
                            {formatPrice(
                                -getConvertedPrice(gc.amount, conversionsRate, false),
                                getConvertedCurrencyCode(conversionsRate, basket?.currency),
                                conversionsRate,
                                shipToCountryCode,
                                intl
                            )}
                        </Text>
                    </Flex>
                ))}
            </Stack>

            {showPromoCodeForm ? (
                <Box w="full" mt="10px">
                    <PromoCode {...promoCodeProps} itemProps={{borderTopWidth: 0}} />
                </Box>
            ) : (
                <Divider color="borderGray" mt={[20, null, null, 16]} />
            )}

            <Flex
                w="full"
                justify="space-between"
                aria-live="polite"
                aria-atomic="true"
                mt="20px"
                mb={[8, null, null, 0]}
            >
                <Text variant="bodyLarge2" color="darkGray">
                    <FormattedMessage defaultMessage="Total" id="order_summary.label.total" />:
                </Text>
                <Text variant="bodyLarge1">
                    {formatPrice(
                        isInternationalShipping && basket?.c_internationalShippingOrderQuote
                            ? basket?.c_basketTotalWithGiftCertificatesApplied != null
                                ? basket?.c_basketTotalWithGiftCertificatesApplied
                                : basket?.c_internationalShippingOrderQuote
                            : getConvertedPrice(
                                  basket?.c_basketTotalWithGiftCertificatesApplied != null
                                      ? basket?.c_basketTotalWithGiftCertificatesApplied
                                      : basket?.orderTotal,
                                  conversionsRate,
                                  false
                              ),
                        getConvertedCurrencyCode(conversionsRate, basket?.currency),
                        conversionsRate,
                        shipToCountryCode,
                        intl
                    )}
                </Text>
            </Flex>

            {/** Tax Comming from SFCC */}
            {!isUS &&
                (!isInternationalShipping ||
                    (isInternationalShipping && isIncludedTax(shipToCountryCode))) && (
                    <Flex
                        w="full"
                        justify="space-between"
                        aria-live="polite"
                        aria-atomic="true"
                        mt="20px"
                        mb={[8, null, null, 0]}
                    >
                        <Text variant="bodyLarge2" color="darkGray">
                            <FormattedMessage
                                defaultMessage="Of which tax"
                                id="order_summary.label.which.tax"
                            />
                            :
                        </Text>
                        {basket.taxTotal != null ? (
                            <Text variant="bodyLarge1">
                                {formatPrice(
                                    getConvertedPrice(basket?.taxTotal, conversionsRate, false),
                                    getConvertedCurrencyCode(conversionsRate, basket?.currency),
                                    conversionsRate,
                                    shipToCountryCode,
                                    intl
                                )}
                            </Text>
                        ) : (
                            <Text variant="bodyLarge1">
                                {formatPrice(
                                    getConvertedPrice(0, conversionsRate, false),
                                    getConvertedCurrencyCode(conversionsRate, basket?.currency),
                                    conversionsRate,
                                    shipToCountryCode,
                                    intl
                                )}
                            </Text>
                        )}
                    </Flex>
                )}
        </>
    )
}

TotalsSummary.propTypes = {
    basket: PropTypes.object,
    showPromoCodeForm: PropTypes.bool,
    giftCertificateLineItemCost: PropTypes.number
}

/**
 * InvoiceSummary is a React functional component that displays a summary of the user's order.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.order - The order object.
 *
 * @returns {JSX.Element|null} The rendered component or null if no order is present.
 */
export const InvoiceSummary = ({order = null}) => {
    const {formatMessage} = useIntl()

    if (!order) {
        return null
    }

    const giftCertificateLineItemCost =
        order?.giftCertificateItems?.reduce((acc, gc) => acc + gc.amount, 0) ?? 0

    return (
        <Stack spacing={5} position={'relative'}>
            <Text
                as="h2"
                variant="descriptor"
                textTransform="uppercase"
                id="invoice-summary-heading"
            >
                {formatMessage(ORDER_SUMMARY_TITLE)}
            </Text>

            <Stack
                spacing={4}
                align="flex-start"
                as="section"
                aria-labelledby="invoice-summary-heading"
            >
                <TotalsSummary
                    basket={order}
                    showPromoCodeForm={false}
                    showDeliveryLine={true}
                    giftCertificateLineItemCost={giftCertificateLineItemCost}
                />
            </Stack>
        </Stack>
    )
}

InvoiceSummary.propTypes = {
    order: PropTypes.object
}

/**
 * OrderSummary is a React functional component that displays a summary of the user's order.
 * It provides an overview of the items in the basket, the total cost, and optionally includes
 * a promo code form and delivery line information.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.basket - The basket object containing order details.
 * @param {boolean} [props.showPromoCodeForm=false] - Determines whether to display the promo code form.
 * @param {boolean} [props.showCartItems=false] - Determines whether to display the cart items.
 * @param {boolean} [props.showDeliveryLine=true] - Determines whether to display the delivery line.
 *
 * @returns {JSX.Element|null} The rendered component or null if no basket ID or order number is present.
 *
 * @example
 * <OrderSummary
 *   basket={basketData}
 *   showPromoCodeForm={true}
 *   showCartItems={true}
 *   showDeliveryLine={false}
 * />
 *
 * @component
 */
const OrderSummary = ({
    showPromoCodeForm = false,
    showCartItems = false,
    showDeliveryLine = true,
    basket = null,
    id = 'order-summary'
}) => {
    const {formatMessage} = useIntl()

    const isDesktop = useBreakpointValue({base: false, lg: true})
    // Exclude unselected bonus gift products
    let {
        data: currentBasket,
        derivedData: {
            totalItems,
            bonusProductLineItemsCount,
            giftCertificateLineItemCost,
            isBuyingOnlyGiftCards
        }
    } = useCurrentBasket()

    if (basket) {
        currentBasket = basket
    }

    if (!currentBasket?.basketId && !currentBasket?.orderNo) {
        return null
    }

    return (
        <Stack data-testid={`sf-${id}`} spacing={5} position={'relative'}>
            {!showCartItems && (
                <Box id={`${id}-heading`} p={0}>
                    <Text
                        variant="descriptor"
                        textTransform="uppercase"
                        role="heading"
                        aria-level="2"
                    >
                        {formatMessage(ORDER_SUMMARY_TITLE)}
                    </Text>
                </Box>
            )}

            <Stack
                spacing={4}
                align="flex-start"
                as="section"
                aria-labelledby="order-summary-heading"
            >
                {showCartItems && (
                    <SummaryAccordion
                        basket={currentBasket}
                        showPromoCodeForm={showPromoCodeForm}
                        showDeliveryLine={showDeliveryLine}
                        isBuyingOnlyGiftCards={isBuyingOnlyGiftCards}
                        giftCertificateLineItemCost={giftCertificateLineItemCost}
                        currentBasket={currentBasket}
                        totalItems={totalItems + bonusProductLineItemsCount}
                        mt="10px"
                    />
                )}
                {(!showCartItems || isDesktop) && (
                    <>
                        {showCartItems && <Divider color="borderGray" mt={14} />}
                        <TotalsSummary
                            basket={currentBasket}
                            showPromoCodeForm={showPromoCodeForm}
                            showDeliveryLine={showDeliveryLine}
                            giftCertificateLineItemCost={giftCertificateLineItemCost}
                        />
                    </>
                )}
            </Stack>
        </Stack>
    )
}

OrderSummary.propTypes = {
    basket: PropTypes.object,
    showPromoCodeForm: PropTypes.bool,
    showCartItems: PropTypes.bool,
    showDeliveryLine: PropTypes.bool,
    id: PropTypes.string
}

export default OrderSummary
